import React from "react";
import { Controller } from "react-hook-form";
import CreatableSelect from "react-select/creatable";

function StudentElementsUI({
  fields,
  control,
  register,
  errors,
  watch,
  elementsData,
  append,
  remove,
  handleAttachmentFileChange,
  fileCounts,
  fileUrls,
  setValue,
  getValues,
  trigger,
}) {
  const handleChangeOfInputWithLengthCheck = (fieldName) => (e) => {
    const value = e.target.value;
    setValue(fieldName, value, { shouldValidate: true });
  };

  return (
    <div className="elements-container">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0 text-primary">Student Elements</h5>
        <button
          className="btn btn-primary btn-sm"
          type="button"
          onClick={() =>
            append({
              id: 0,
              name: "",
              description: "",
              createdBy: localStorage.getItem("userId"),
              elementDetail: {
                value: "",
                description: "",
                effectiveDate: null,
                endDate: null,
                createdBy: localStorage.getItem("userId"),
                fileName: "",
                filePath: "",
                base64String: "",
              },
            })
          }
        >
          <i className="fas fa-plus me-1"></i> Add Element
        </button>
      </div>

      {fields.map((field, index) => (
        <div className="card mb-4 border-0 shadow-sm" key={field.id} style={{ borderRadius: "12px", background: "#f8f9fa" }}>
          <div className="card-body p-4">
            <div className="row g-4 mb-4">
              {/* First Row: Name, Value, Dates */}
              <div className="col-md-3">
                <label className="form-label fw-bold mini-label">Element Name</label>
                <Controller
                  name={`elements[${index}].name`}
                  control={control}
                  rules={{
                    required: "Element name is required",
                  }}
                  render={({ field }) => (
                    <CreatableSelect
                      {...field}
                      value={field.value || null}
                      options={elementsData.map((element) => ({
                        value: element.id,
                        label: element.name,
                      }))}
                      onChange={(selectedOption, actionMeta) => {
                        if (actionMeta.action === "create-option") {
                          setValue(`elements[${index}].name`, {
                            value: 0,
                            label: selectedOption.label,
                          });
                        } else if (actionMeta.action === "clear") {
                          setValue(`elements[${index}].name`, null);
                        } else {
                          setValue(
                            `elements[${index}].name`,
                            selectedOption || null
                          );
                        }
                        trigger(`elements[${index}].name`);
                      }}
                      classNamePrefix="react-select"
                      placeholder="Select or Create..."
                      isClearable
                    />
                  )}
                />
                {errors.elements?.[index]?.name && (
                  <small className="text-danger mt-1 d-block">
                    {errors.elements[index].name.message}
                  </small>
                )}
              </div>

              <div className="col-md-3">
                <label className="form-label fw-bold mini-label">Value</label>
                <input
                  {...register(`elements[${index}].elementDetail.value`, {
                    required: watch(`elements[${index}].name`)
                      ? "This field is required."
                      : false,
                  })}
                  type="text"
                  className={`form-control ${errors.elements?.[index]?.elementDetail?.value ? 'is-invalid' : ''}`}
                  placeholder="Enter value"
                />
                {errors.elements?.[index]?.elementDetail?.value && (
                  <small className="text-danger mt-1 d-block">
                    {errors.elements[index].elementDetail.value.message}
                  </small>
                )}
              </div>

              <div className="col-md-3">
                <label className="form-label fw-bold mini-label">Effective Date</label>
                <input
                  {...register(`elements[${index}].elementDetail.effectiveDate`, {
                    validate: {
                      lessThanEndDate: (value) => {
                        const endDate = getValues(
                          `elements[${index}].elementDetail.endDate`
                        );
                        return (
                          !value || 
                          !endDate ||
                          new Date(value) <= new Date(endDate) ||
                          "Must be on or before End Date."
                        );
                      },
                    },
                  })}
                  type="date"
                  className="form-control"
                />
                {errors.elements?.[index]?.elementDetail?.effectiveDate && (
                  <small className="text-danger mt-1 d-block">
                    {errors.elements[index].elementDetail.effectiveDate.message}
                  </small>
                )}
              </div>

              <div className="col-md-3">
                <label className="form-label fw-bold mini-label">End Date</label>
                <input
                  {...register(`elements[${index}].elementDetail.endDate`, {
                    validate: {
                      greaterThanEffectiveDate: (value) => {
                        const effectiveDate = getValues(
                          `elements[${index}].elementDetail.effectiveDate`
                        );
                        return (
                          !value || 
                          !effectiveDate ||
                          new Date(value) >= new Date(effectiveDate) ||
                          "Must be on or after Effective Date."
                        );
                      },
                    },
                  })}
                  type="date"
                  className="form-control"
                />
                {errors.elements?.[index]?.elementDetail?.endDate && (
                  <small className="text-danger mt-1 d-block">
                    {errors.elements[index].elementDetail.endDate.message}
                  </small>
                )}
              </div>
            </div>

            <div className="row g-4 align-items-end">
              {/* Second Row: Description, Attachment, Action */}
              <div className="col-md-7">
                <label className="form-label fw-bold mini-label">Description</label>
                <textarea
                  {...register(`elements[${index}].elementDetail.description`, {
                    maxLength: {
                      value: 250,
                      message: "Maximum of 250 characters allowed.",
                    },
                  })}
                  className="form-control"
                  rows="1"
                  placeholder="Add a brief description..."
                  style={{ resize: "none" }}
                  onChange={handleChangeOfInputWithLengthCheck(
                    `elements[${index}].elementDetail.description`
                  )}
                />
                {errors.elements?.[index]?.elementDetail?.description && (
                  <small className="text-danger mt-1 d-block">
                    {errors.elements[index].elementDetail.description.message}
                  </small>
                )}
              </div>

              <div className="col-md-3">
                <label className="form-label fw-bold mini-label">Document Attachment</label>
                <div className="d-flex align-items-center">
                  <div className="position-relative flex-grow-1">
                    <button
                      className="btn btn-outline-secondary w-100 d-flex align-items-center justify-content-center"
                      type="button"
                    >
                      <i className="fas fa-upload me-2"></i>
                      <span>{fileCounts[index] ? "Change File" : "Upload"}</span>
                      <input
                        type="file"
                        className="attachment"
                        onChange={(event) =>
                          handleAttachmentFileChange(event, index)
                        }
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          opacity: 0,
                          cursor: "pointer",
                        }}
                      />
                    </button>
                  </div>
                  {fileCounts[index] ? (
                    <a
                      href={fileUrls[index]}
                      title="Preview Attachment"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ms-3 btn btn-info btn-sm text-white"
                      style={{ borderRadius: "50%", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center" }}
                    >
                      <i className="fa fa-eye"></i>
                    </a>
                  ) : null}
                </div>
              </div>

              <div className="col-md-2 d-flex justify-content-end">
                {fields.length > 1 && (
                  <button
                    className="btn btn-danger btn-sm"
                    type="button"
                    onClick={() => remove(index)}
                    title="Remove Element"
                  >
                    <i className="fas fa-trash-alt me-1"></i> Remove
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
      
      <style>{`
        .mini-label {
          font-size: 0.75rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          color: #6c757d;
        }
        .react-select__control {
          border-color: #dee2e6 !important;
          border-radius: 0.375rem !important;
        }
        .react-select__placeholder {
          font-size: 0.9rem;
        }
      `}</style>
    </div>
  );
}

export default StudentElementsUI;
